'use client';

import { useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useSiteConfig } from '@/lib/site-config';
import { useToast } from '@/lib/toast';
import { getRecaptchaToken } from '@/lib/recaptcha';

const REASONS: { kind: string; label: string }[] = [
  { kind: 'spam',      label: 'Spam or advertising' },
  { kind: 'abuse',     label: 'Harassment or abuse' },
  { kind: 'copyright', label: 'Copyright violation' },
  { kind: 'nsfw',      label: 'Sexual or NSFW content' },
  { kind: 'other',     label: 'Something else' },
];

/**
 * Compact "Report" button that opens a modal with a reason picker + a free-text
 * note. Server dedupes per reporter+target so re-clicking is harmless.
 */
export default function ReportButton({ promptId }: { promptId: string }) {
  const { user } = useAuth();
  const { config } = useSiteConfig();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [reasonKind, setReasonKind] = useState('spam');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  if (!user) return null;  // Anonymous reports aren't supported.
  // Admin master switch — hide the button entirely when reporting is disabled.
  // The backend ALSO rejects POST /api/reports with 403, but hiding the entry
  // point avoids the confusing "click → toast error" round-trip.
  if (!config['reports.enabled']) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const recaptchaToken = await getRecaptchaToken('report', String(config['recaptcha.site_key'] ?? ''));
      await api.reports.file(
        { target_type: 'prompt', target_id: promptId, reason_kind: reasonKind, note: note.trim() || undefined },
        recaptchaToken,
      );
      toast.success('Thanks — a moderator will review this.');
      setOpen(false);
      setNote('');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Couldn’t file report.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Report this prompt"
        aria-label="Report this prompt"
        className="flex items-center justify-center w-12 h-12 rounded-full border border-outline-variant text-on-surface-variant hover:border-error hover:text-error transition-colors"
      >
        <span className="material-symbols-outlined" style={{ fontSize: 20 }}>flag</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          style={{ background: 'color-mix(in oklch, black 40%, transparent)', backdropFilter: 'blur(6px)' }}
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-raised)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-lg)',
              padding: 24, width: 480, maxWidth: '100%',
            }}
          >
            <div style={{
              fontSize: 11, fontWeight: 600, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 4,
            }}>
              Report
            </div>
            <div style={{
              fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, marginBottom: 12,
            }}>
              What&rsquo;s wrong with this prompt?
            </div>
            <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16 }}>
              A moderator will review. False or repeated reports may affect your account.
            </p>

            <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
              {REASONS.map((r) => (
                <label key={r.kind} style={{
                  display: 'flex', gap: 10, alignItems: 'center',
                  padding: '8px 12px',
                  border: `1px solid ${reasonKind === r.kind ? 'var(--accent)' : 'var(--line)'}`,
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  background: reasonKind === r.kind ? 'rgba(56, 189, 248, 0.08)' : 'transparent',
                }}>
                  <input
                    type="radio"
                    name="report_reason"
                    checked={reasonKind === r.kind}
                    onChange={() => setReasonKind(r.kind)}
                  />
                  <span style={{ fontSize: 13 }}>{r.label}</span>
                </label>
              ))}
            </div>

            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 1000))}
              rows={3}
              placeholder="Optional — anything that helps the moderator."
              className="ps-input"
              style={{ resize: 'vertical', minHeight: 70 }}
            />
            <div style={{
              fontSize: 11, color: note.length >= 950 ? '#f59e0b' : 'var(--ink-3)',
              textAlign: 'right', marginTop: 4, marginBottom: 12,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {note.length} / 1000
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setOpen(false)} className="ps-btn ps-btn-ghost ps-btn-sm">Cancel</button>
              <button
                onClick={submit}
                disabled={busy}
                aria-busy={busy || undefined}
                className="ps-btn ps-btn-primary ps-btn-sm"
              >
                {busy ? 'Sending…' : 'Send report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
